import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TaskDialog } from "@/components/TaskDialog";
import { OpportunityDialog } from "@/components/OpportunityDialog";
import { PipelineManager } from "@/components/PipelineManager";
import { FollowUpBadge, followUpState } from "@/components/FollowUpBadge";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, Plus, Trash2, CheckCircle2, Circle, Columns3, BellRing, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function DueBadge({ task }) {
  if (!task.due_date) return null;
  if (task.status === "done") return <Badge data-testid="task-due-badge" variant="secondary">Concluída</Badge>;
  const late = task.status !== "done" && task.due_date.slice(0, 10) < today();
  return (
    <Badge data-testid="task-due-badge" className={late ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"}>
      {late ? "Vencida" : "No prazo"} · {new Date(task.due_date.slice(0, 10) + "T12:00:00").toLocaleDateString("pt-BR")}
    </Badge>
  );
}

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/tasks", { params: { mine } }).then(({ data }) => setTasks(data));
  useEffect(() => { load(); }, [mine]);

  const toggle = async (t) => { await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" }); load(); };
  const remove = async (t) => { if (window.confirm("Excluir tarefa?")) { await api.delete(`/tasks/${t.id}`); load(); } };
  const late = tasks.filter((t) => t.status !== "done" && t.due_date && t.due_date.slice(0, 10) < today()).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={mine ? "default" : "outline"} onClick={() => setMine(!mine)} className={mine ? "bg-emerald-600" : ""}>{mine ? "Minhas" : "Todas"}</Button>
        {late > 0 && <Badge className="bg-rose-100 text-rose-800" data-testid="tasks-overdue-count">{late} vencida(s)</Badge>}
        <Button size="sm" onClick={() => setOpen(true)} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="task-new-button"><Plus className="w-4 h-4 mr-1" /> Nova tarefa</Button>
      </div>
      <Card><CardContent className="p-0">
        {tasks.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma tarefa.</div>}
        {tasks.map((t) => (
          <div key={t.id} className="flex items-start gap-3 p-4 border-b border-border" data-testid="task-row">
            <button onClick={() => toggle(t)} className="mt-0.5" data-testid="task-toggle-button">
              {t.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-muted-foreground" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`font-semibold ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
              {t.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.description}</div>}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground">
                <DueBadge task={t} />
                {["alta", "urgente"].includes(t.priority) && <Badge variant="outline" className="capitalize">{t.priority}</Badge>}
                <span>{t.assignee_name || "—"}</span>
                {t.contact_phone && <span className="mono">· +{t.contact_phone}</span>}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(t)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </CardContent></Card>
      <TaskDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

const OppCard = ({ o, status, onOpen, onChat }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("id", o.id)} onClick={() => onOpen(o)} data-testid="kanban-card"
       className="p-2.5 mb-2 rounded-lg bg-card border border-border cursor-pointer shadow-sm hover:border-emerald-500/60 transition-colors">
    <div className="flex items-start gap-2">
      <span className="w-3 h-3 rounded-full mt-1 shrink-0 border border-border" style={{ background: status?.color || "transparent" }}
            title={status?.name || "Sem status"} data-testid="kanban-card-status" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-snug">{o.title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{o.contact_name || o.company || "—"} · {brl(o.value)}</div>
      </div>
      {o.chat_id && (
        <button onClick={(e) => { e.stopPropagation(); onChat(o.chat_id); }} title="Abrir conversa" data-testid="kanban-card-chat"
                className="text-emerald-600 hover:text-emerald-700"><MessageCircle className="w-4 h-4" /></button>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-1 mt-1.5">
      <FollowUpBadge iso={o.follow_up_at} />
      {o.owner_name && <span className="text-[10px] text-muted-foreground">{o.owner_name}</span>}
    </div>
  </div>
);

function FunnelTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [stages, setStages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [manage, setManage] = useState(false);
  const [onlyFollow, setOnlyFollow] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const load = () => Promise.all([api.get("/opportunities"), api.get("/pipeline/stages"), api.get("/opp-statuses")])
    .then(([o, s, st]) => { setOpps(o.data); setStages(s.data); setStatuses(st.data); });
  useEffect(() => { load(); }, []);

  const move = async (id, stage) => { await api.patch(`/opportunities/${id}`, { stage }); load(); };
  const pending = opps.filter((o) => ["late", "today"].includes(followUpState(o.follow_up_at)));
  const visible = opps.filter((o) => (!onlyFollow || pending.includes(o)) && (!statusFilter || o.status_id === statusFilter));
  const statusOf = (id) => statuses.find((s) => s.id === id);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={onlyFollow ? "default" : "outline"} onClick={() => setOnlyFollow(!onlyFollow)}
                className={onlyFollow ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="opp-followup-filter">
          <BellRing className="w-4 h-4 mr-1" /> Retornos pendentes ({pending.length})
        </Button>
        {statuses.map((s) => (
          <button key={s.id} onClick={() => setStatusFilter(statusFilter === s.id ? null : s.id)} data-testid={`opp-filter-status-${s.id}`}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${statusFilter === s.id ? "border-foreground bg-muted font-semibold" : "border-border"}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /> {s.name}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {user?.role === "admin" && <Button size="sm" variant="outline" onClick={() => setManage(true)} data-testid="pipeline-manage-button"><Columns3 className="w-4 h-4 mr-1" /> Personalizar funil</Button>}
          <Button size="sm" onClick={() => setEditing({})} className="bg-emerald-600 hover:bg-emerald-700" data-testid="opp-new-button"><Plus className="w-4 h-4 mr-1" /> Nova oportunidade</Button>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="crm-kanban">
        {stages.map((st) => {
          const col = visible.filter((o) => o.stage === st.id);
          return (
            <div key={st.id} className="w-72 shrink-0 rounded-xl bg-muted/40 border border-border p-2 min-h-[260px]"
                 onDragOver={(e) => e.preventDefault()} onDrop={(e) => move(e.dataTransfer.getData("id"), st.id)} data-testid={`kanban-col-${st.id}`}>
              <div className="h-1 rounded-full mb-2" style={{ background: st.color }} />
              <div className="text-xs font-bold uppercase tracking-wider px-1 mb-2 flex justify-between gap-2">
                <span className="truncate">{st.name}</span>
                <span className="text-muted-foreground shrink-0">{col.length} · {brl(col.reduce((s, o) => s + (o.value || 0), 0))}</span>
              </div>
              {col.map((o) => <OppCard key={o.id} o={o} status={statusOf(o.status_id)} onOpen={setEditing} onChat={(id) => navigate(`/inbox/${id}`)} />)}
            </div>
          );
        })}
      </div>
      <OpportunityDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} initial={editing} stages={stages} statuses={statuses} onSaved={load} />
      <PipelineManager open={manage} onOpenChange={setManage} stages={stages} statuses={statuses} onChanged={load} />
    </div>
  );
}

export default function CrmPage() {
  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <h1 className="heading text-3xl font-extrabold flex items-center gap-2"><Briefcase className="w-7 h-7 text-emerald-600" /> CRM</h1>
        <Tabs defaultValue="funnel">
          <TabsList><TabsTrigger value="funnel" data-testid="crm-tab-funnel">Funil de vendas</TabsTrigger><TabsTrigger value="tasks" data-testid="crm-tab-tasks">Tarefas</TabsTrigger></TabsList>
          <TabsContent value="tasks" className="mt-4"><TasksTab /></TabsContent>
          <TabsContent value="funnel" className="mt-4"><FunnelTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
